import { Injectable, inject, signal } from "@angular/core";
import type { TeamDTO, TeamSummaryDTO } from "@magpie/types";
import { TrpcService } from "./trpc.service";

/**
 * Frontend wrapper for the team tRPC router. Caches the caller's team list
 * as a signal so the UI can react to mutations without bouncing through a
 * full reload.
 */
@Injectable({ providedIn: "root" })
export class TeamService {
	private readonly trpc = inject(TrpcService);

	readonly myTeams = signal<TeamSummaryDTO[]>([]);
	readonly loadingMyTeams = signal(false);

	async refreshMyTeams(): Promise<TeamSummaryDTO[]> {
		this.loadingMyTeams.set(true);
		try {
			const teams = await this.trpc.client.team.list.query();
			this.myTeams.set(teams);
			return teams;
		} finally {
			this.loadingMyTeams.set(false);
		}
	}

	async get(teamId: string): Promise<TeamDTO> {
		return this.trpc.client.team.get.query({ teamId });
	}

	async create(name: string): Promise<TeamDTO> {
		const team = await this.trpc.client.team.create.mutate({ name });
		await this.refreshMyTeams();
		return team;
	}

	async rename(teamId: string, name: string): Promise<TeamDTO> {
		const team = await this.trpc.client.team.rename.mutate({ teamId, name });
		await this.refreshMyTeams();
		return team;
	}

	async delete(teamId: string): Promise<void> {
		await this.trpc.client.team.delete.mutate({ teamId });
		await this.refreshMyTeams();
	}

	async regenerateCode(teamId: string): Promise<TeamDTO> {
		const team = await this.trpc.client.team.regenerateCode.mutate({ teamId });
		await this.refreshMyTeams();
		return team;
	}

	async joinByCode(code: string): Promise<TeamDTO> {
		const team = await this.trpc.client.team.joinByCode.mutate({
			code: code.trim().toUpperCase(),
		});
		await this.refreshMyTeams();
		return team;
	}

	async leave(teamId: string): Promise<void> {
		await this.trpc.client.team.leave.mutate({ teamId });
		await this.refreshMyTeams();
	}

	async addGhost(teamId: string, nickname: string): Promise<TeamDTO> {
		return this.trpc.client.team.addGhost.mutate({ teamId, nickname });
	}

	async removeMember(teamId: string, memberId: string): Promise<TeamDTO> {
		return this.trpc.client.team.removeMember.mutate({ teamId, memberId });
	}

	async setMemberActive(
		teamId: string,
		memberId: string,
		isActive: boolean,
	): Promise<TeamDTO> {
		return this.trpc.client.team.setMemberActive.mutate({
			teamId,
			memberId,
			isActive,
		});
	}

	async setPurchaseTeam(
		purchaseId: string,
		teamId: string | null,
	): Promise<void> {
		await this.trpc.client.team.setPurchaseTeam.mutate({ purchaseId, teamId });
	}
}
